import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { DocumentTextIcon, EyeIcon, MailIcon, PencilIcon, TrashIcon } from './Icons';

export type TableActionTone = 'view' | 'edit' | 'delete' | 'summary' | 'notify' | 'neutral';

const toneClasses: Record<TableActionTone, string> = {
  view: 'text-teal-600 hover:text-teal-800 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-900/30',
  edit: 'text-sky-600 hover:text-sky-800 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/30',
  delete: 'text-red-600 hover:text-red-900 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30',
  summary:
    'text-amber-600 hover:text-amber-900 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/30',
  notify:
    'text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30',
  neutral:
    'text-slate-600 hover:text-slate-900 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/60',
};

const actionBaseClass =
  'inline-flex items-center justify-center p-1 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40';

interface TableActionsProps {
  children: React.ReactNode;
  className?: string;
}

/** Compact row for table action icons (View / Edit / Delete / …). */
export const TableActions: React.FC<TableActionsProps> = ({ children, className }) => (
  <div className={cn('flex flex-nowrap items-center gap-0.5', className)}>{children}</div>
);

interface TableActionLinkProps {
  to: string;
  title: string;
  tone?: TableActionTone;
  children: React.ReactNode;
  className?: string;
  'aria-label'?: string;
}

export const TableActionLink: React.FC<TableActionLinkProps> = ({
  to,
  title,
  tone = 'neutral',
  children,
  className,
  'aria-label': ariaLabel,
}) => (
  <Link
    to={to}
    title={title}
    aria-label={ariaLabel ?? title}
    onClick={(e) => e.stopPropagation()}
    className={cn(actionBaseClass, toneClasses[tone], className)}
  >
    {children}
  </Link>
);

interface TableActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title: string;
  tone?: TableActionTone;
  children: React.ReactNode;
}

export const TableActionButton: React.FC<TableActionButtonProps> = ({
  title,
  tone = 'neutral',
  children,
  className,
  onClick,
  type = 'button',
  ...props
}) => (
  <button
    type={type}
    title={title}
    aria-label={props['aria-label'] ?? title}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    className={cn(actionBaseClass, toneClasses[tone], className)}
    {...props}
  >
    {children}
  </button>
);

export const TableViewIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <EyeIcon className={className} />
);

export const TableEditIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <PencilIcon className={className} />
);

export const TableDeleteIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <TrashIcon className={className} />
);

export const TableSummaryIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <DocumentTextIcon className={className} />
);

export const TableNotifyIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <MailIcon className={className} />
);

export interface StandardTableActionsProps {
  viewTo?: string;
  editTo?: string;
  onDelete?: () => void;
  onSummary?: () => void;
  summaryTo?: string;
  onNotify?: () => void;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  /** Extra actions rendered after the standard set. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * Shared View / Edit / Delete (and optional Summary / Notify) actions for list tables.
 */
export const StandardTableActions: React.FC<StandardTableActionsProps> = ({
  viewTo,
  editTo,
  onDelete,
  onSummary,
  summaryTo,
  onNotify,
  canView = true,
  canEdit = true,
  canDelete = true,
  children,
  className,
}) => (
  <TableActions className={className}>
    {onSummary && (
      <TableActionButton title="Summary" tone="summary" onClick={onSummary}>
        <TableSummaryIcon />
      </TableActionButton>
    )}
    {summaryTo && (
      <TableActionLink to={summaryTo} title="Summary" tone="summary">
        <TableSummaryIcon />
      </TableActionLink>
    )}
    {canView && viewTo && (
      <TableActionLink to={viewTo} title="View" tone="view">
        <TableViewIcon />
      </TableActionLink>
    )}
    {canEdit && editTo && (
      <TableActionLink to={editTo} title="Edit" tone="edit">
        <TableEditIcon />
      </TableActionLink>
    )}
    {onNotify && (
      <TableActionButton title="Notify" tone="notify" onClick={onNotify}>
        <TableNotifyIcon />
      </TableActionButton>
    )}
    {canDelete && onDelete && (
      <TableActionButton title="Delete" tone="delete" onClick={onDelete}>
        <TableDeleteIcon />
      </TableActionButton>
    )}
    {children}
  </TableActions>
);

export default StandardTableActions;
